// frontend/src/components/vacancy/VacancyForm.tsx

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'
import { Select } from '@/components/common/UI/Select'
import { Card } from '@/components/common/UI/Card'
import { useToast } from '@/components/common/UI/Toast'
import { useAuthStore } from '@/store'
import { CompanySelect } from '@/components/company/CompanyList'
import { CityAutocomplete } from '@/components/common/CityAutocomplete'
import {
  EMPLOYMENT_TYPE_LABELS,
  WORK_SCHEDULE_LABELS,
  EXPERIENCE_LEVEL_LABELS,
} from '@/utils/constants'

// ============================================
// Схема валидации
// ============================================

const vacancySchema = z.object({
  title: z.string().min(3, 'Название должно содержать минимум 3 символа').max(255),
  description: z.string().min(10, 'Описание должно содержать минимум 10 символов'),
  company_id: z.number().min(1, 'Выберите компанию'),
  responsibilities: z.array(z.string()).optional(),
  requirements: z.array(z.string()).optional(),
  conditions: z.array(z.string()).optional(),
  experience_level: z.string().optional(),
  salary_from: z.number().min(0).optional().or(z.null()),
  salary_to: z.number().min(0).optional().or(z.null()),
  salary_currency: z.string().default('RUB'),
  show_salary: z.boolean().default(true),
  employment_types: z.array(z.string()).min(1, 'Выберите хотя бы один тип занятости'),
  work_schedules: z.array(z.string()).min(1, 'Выберите хотя бы один график работы'),
  city: z.string().max(100).optional(),
  country: z.string().default('Россия'),
  latitude: z.number().optional().or(z.null()),
  longitude: z.number().optional().or(z.null()),
  remote_available: z.boolean().default(false),
  key_skills: z.array(z.string()).optional(),
  original_language: z.string().default('ru'),
  available_languages: z.array(z.string()).default(['ru']),
})

type VacancyFormData = z.infer<typeof vacancySchema>

// ============================================
// Компонент формы
// ============================================

interface VacancyFormProps {
  vacancy?: any
  onSuccess?: () => void
  onCancel?: () => void
}

export const VacancyForm: React.FC<VacancyFormProps> = ({
  vacancy,
  onSuccess,
  onCancel
}) => {
  const { user } = useAuthStore()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [newSkill, setNewSkill] = useState('')
  const [newResponsibility, setNewResponsibility] = useState('')
  const [newRequirement, setNewRequirement] = useState('')
  const [newCondition, setNewCondition] = useState('')
  const [aiBrief, setAiBrief] = useState('')
  const descriptionInputRef = React.useRef<HTMLTextAreaElement | null>(null)
  const aiAccess = useQuery({ queryKey: ['billing','me'], queryFn: () => apiClient.get<any>('/v1/billing/me'), enabled: user?.role === 'employer' })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<VacancyFormData>({
    resolver: zodResolver(vacancySchema),
    defaultValues: {
      title: vacancy?.title || '',
      description: vacancy?.description || '',
      company_id: vacancy?.company_id || undefined,
      responsibilities: vacancy?.responsibilities || [],
      requirements: vacancy?.requirements || [],
      conditions: vacancy?.conditions || [],
      experience_level: vacancy?.experience_level || '',
      salary_from: vacancy?.salary_from || null,
      salary_to: vacancy?.salary_to || null,
      salary_currency: vacancy?.salary_currency || 'RUB',
      show_salary: vacancy?.show_salary ?? true,
      employment_types: vacancy?.employment_types || [],
      work_schedules: vacancy?.work_schedules || [],
      city: vacancy?.city || '',
      country: vacancy?.country || 'Россия',
      remote_available: vacancy?.remote_available || false,
      key_skills: vacancy?.key_skills || [],
      original_language: vacancy?.original_language || 'ru',
      available_languages: vacancy?.available_languages || ['ru'],
    }
  })

  const watchEmploymentTypes = watch('employment_types')
  const watchWorkSchedules = watch('work_schedules')
  const watchKeySkills = watch('key_skills')
  const watchDescription = watch('description') || ''
  const descriptionField = register('description')

  const formatDescription = (kind: 'bold' | 'italic' | 'bullet' | 'numbered' | 'heading') => {
    const input = descriptionInputRef.current
    if (!input) return
    const value = watchDescription
    const start = input.selectionStart
    const end = input.selectionEnd
    const selected = value.slice(start, end)
    let next = value
    let selectionStart = start
    let selectionEnd = end
    if (kind === 'bold' || kind === 'italic') {
      const marker = kind === 'bold' ? '**' : '*'
      const content = selected || 'текст'
      const formatted = `${marker}${content}${marker}`
      next = value.slice(0, start) + formatted + value.slice(end)
      selectionStart = start + marker.length
      selectionEnd = selectionStart + content.length
    } else {
      const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1
      const lineEndIndex = value.indexOf('\n', end)
      const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex
      const lines = value.slice(lineStart, lineEnd).split('\n')
      const prefix = kind === 'heading' ? '## ' : kind === 'numbered' ? '1. ' : '- '
      const formatted = lines.map((line, index) => `${kind === 'numbered' ? `${index + 1}. ` : prefix}${line}`).join('\n')
      next = value.slice(0, lineStart) + formatted + value.slice(lineEnd)
      selectionStart = lineStart
      selectionEnd = lineStart + formatted.length
    }
    setValue('description', next, { shouldDirty: true, shouldValidate: true })
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(selectionStart, selectionEnd) })
  }

  // Получение компаний пользователя
  const { isLoading: companiesLoading } = useQuery({
    queryKey: ['companies', 'my'],
    queryFn: async () => {
      return apiClient.get('/v1/companies/my')
    },
    enabled: !!user
  })

  // Мутация для создания/обновления вакансии
  const mutation = useMutation({
    mutationFn: async (data: VacancyFormData) => {
      // Очищаем пустые строки
      const payload = {
        ...data,
        key_skills: data.key_skills?.filter(s => s.trim()) || [],
        responsibilities: data.responsibilities?.filter(s => s.trim()) || [],
        requirements: data.requirements?.filter(s => s.trim()) || [],
        conditions: data.conditions?.filter(s => s.trim()) || [],
        salary_from: data.salary_from || null,
        salary_to: data.salary_to || null,
      }

      if (vacancy) {
        return apiClient.put(`/v1/vacancies/${vacancy.id}`, payload)
      }
      return apiClient.post('/v1/vacancies', payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacancies'] })
      if (vacancy) {
        queryClient.invalidateQueries({ queryKey: ['vacancy', vacancy.id] })
      }
      showToast(
        vacancy ? 'Вакансия обновлена' : 'Вакансия создана',
        'success'
      )
      if (onSuccess) onSuccess()
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.detail || 'Произошла ошибка',
        'error'
      )
    }
  })

  const generateDraft = useMutation({
    mutationFn: () => apiClient.post<any>('/v1/ai-assistant/vacancy-draft', { brief: aiBrief.trim() }),
    onSuccess: (draft: any) => {
      setValue('title', draft.title || '')
      setValue('description', draft.description || '')
      setValue('responsibilities', draft.responsibilities || [])
      setValue('requirements', draft.requirements || [])
      setValue('conditions', draft.conditions || [])
      setValue('key_skills', draft.key_skills || [])
      setValue('experience_level', draft.experience_level || 'no_experience')
      showToast(draft.fallback ? 'Локальная модель недоступна: вставлен черновик из вашего описания' : 'Черновик создан локальной AI-моделью. Проверьте и отредактируйте текст.', draft.fallback ? 'warning' : 'success')
    },
    onError: (error: any) => showToast(error.response?.data?.detail || 'Не удалось создать черновик. Проверьте тариф и доступность локальной модели.', 'error'),
  })

  const onSubmit = (data: VacancyFormData) => {
    // Валидация зарплаты
    if (data.salary_from && data.salary_to && data.salary_from > data.salary_to) {
      showToast('Зарплата "до" должна быть больше зарплаты "от"', 'error')
      return
    }

    mutation.mutate(data)
  }

  // Добавление навыка
  const addSkill = () => {
    if (!newSkill.trim()) return
    const current = watchKeySkills || []
    if (current.includes(newSkill.trim())) {
      showToast('Этот навык уже добавлен', 'warning')
      return
    }
    setValue('key_skills', [...current, newSkill.trim()])
    setNewSkill('')
  }

  const removeSkill = (skill: string) => {
    const current = watchKeySkills || []
    setValue('key_skills', current.filter(s => s !== skill))
  }

  // Добавление обязанности
  const addResponsibility = () => {
    if (!newResponsibility.trim()) return
    const current = watch('responsibilities') || []
    setValue('responsibilities', [...current, newResponsibility.trim()])
    setNewResponsibility('')
  }

  const removeResponsibility = (index: number) => {
    const current = watch('responsibilities') || []
    setValue('responsibilities', current.filter((_, i) => i !== index))
  }

  // Добавление требования
  const addRequirement = () => {
    if (!newRequirement.trim()) return
    const current = watch('requirements') || []
    setValue('requirements', [...current, newRequirement.trim()])
    setNewRequirement('')
  }

  const removeRequirement = (index: number) => {
    const current = watch('requirements') || []
    setValue('requirements', current.filter((_, i) => i !== index))
  }

  // Добавление условия
  const addCondition = () => {
    if (!newCondition.trim()) return
    const current = watch('conditions') || []
    setValue('conditions', [...current, newCondition.trim()])
    setNewCondition('')
  }

  const removeCondition = (index: number) => {
    const current = watch('conditions') || []
    setValue('conditions', current.filter((_, i) => i !== index))
  }

  // Toggle для employment type
  const toggleEmploymentType = (type: string) => {
    const current = watchEmploymentTypes || []
    if (current.includes(type)) {
      setValue('employment_types', current.filter(t => t !== type))
    } else {
      setValue('employment_types', [...current, type])
    }
  }

  // Toggle для work schedule
  const toggleWorkSchedule = (schedule: string) => {
    const current = watchWorkSchedules || []
    if (current.includes(schedule)) {
      setValue('work_schedules', current.filter(s => s !== schedule))
    } else {
      setValue('work_schedules', [...current, schedule])
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card className="border border-violet-200 bg-violet-50/50 p-6">
        <h3 className="text-lg font-semibold text-secondary-900">AI-помощник вакансии</h3>
        <p className="mt-1 text-sm text-secondary-600">Опишите задачу своими словами. Локальная модель подготовит редактируемый черновик; ничего не публикуется без вашего сохранения.</p>
        {aiAccess.data?.ai_assistant === false && <p className="mt-2 text-sm text-amber-800">Функция доступна на тарифе работодателя с AI-помощником. Посмотрите тарифы в профиле.</p>}
        <textarea value={aiBrief} onChange={event => setAiBrief(event.target.value)} minLength={10} maxLength={6000} rows={3} placeholder="Ищем менеджера по продажам B2B: опыт от 2 лет, работа с CRM, офис в Екатеринбурге…" className="mt-3 w-full rounded-lg border border-secondary-300 bg-white px-3 py-2" />
        <Button type="button" className="mt-3" disabled={aiBrief.trim().length < 10 || generateDraft.isPending || aiAccess.data?.ai_assistant === false} onClick={() => generateDraft.mutate()}>{generateDraft.isPending ? 'Готовим черновик…' : 'Создать черновик локальной AI-моделью'}</Button>
      </Card>
      {/* Основная информация */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Основная информация</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Название вакансии *"
              placeholder="Senior Python разработчик"
              error={errors.title?.message}
              {...register('title')}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-secondary-700">
              Описание *
              <span className="mt-1 block font-normal text-secondary-500">Выделите текст или поставьте курсор и выберите форматирование.</span>
              <div className="mt-2 flex flex-wrap gap-1 rounded-t-lg border border-b-0 border-secondary-300 bg-secondary-50 p-2" aria-label="Форматирование описания">
                <button type="button" title="Жирный" aria-label="Жирный" onClick={() => formatDescription('bold')} className="rounded border border-secondary-200 bg-white px-3 py-1 font-bold hover:bg-secondary-100">B</button>
                <button type="button" title="Курсив" aria-label="Курсив" onClick={() => formatDescription('italic')} className="rounded border border-secondary-200 bg-white px-3 py-1 italic hover:bg-secondary-100">I</button>
                <button type="button" onClick={() => formatDescription('heading')} className="rounded border border-secondary-200 bg-white px-3 py-1 text-sm hover:bg-secondary-100">Подзаголовок</button>
                <button type="button" onClick={() => formatDescription('bullet')} className="rounded border border-secondary-200 bg-white px-3 py-1 text-sm hover:bg-secondary-100">• Список</button>
                <button type="button" onClick={() => formatDescription('numbered')} className="rounded border border-secondary-200 bg-white px-3 py-1 text-sm hover:bg-secondary-100">1. Список</button>
              </div>
              <textarea
                placeholder="Подробное описание вакансии..."
                rows={10}
                aria-invalid={Boolean(errors.description)}
                className="w-full rounded-b-lg border border-secondary-300 bg-white px-3 py-2.5 font-normal leading-6 text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                {...descriptionField}
                ref={(element) => { descriptionField.ref(element); descriptionInputRef.current = element }}
              />
              {errors.description?.message && <span className="mt-1 block text-sm text-error">{errors.description.message}</span>}
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-secondary-700">
              Компания *
            </label>
            {companiesLoading ? (
              <div className="text-sm text-secondary-500">Загрузка компаний...</div>
            ) : (
              <CompanySelect
                value={watch('company_id')}
                onChange={(value) => setValue('company_id', value)}
              />
            )}
            {errors.company_id && (
              <p className="mt-1.5 text-sm text-error">{errors.company_id.message}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Зарплата */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Зарплата</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Зарплата от"
            type="number"
            placeholder="100000"
            error={errors.salary_from?.message}
            {...register('salary_from', { valueAsNumber: true })}
          />

          <Input
            label="Зарплата до"
            type="number"
            placeholder="200000"
            error={errors.salary_to?.message}
            {...register('salary_to', { valueAsNumber: true })}
          />

          <Select
            label="Валюта"
            options={[
              { value: 'RUB', label: '₽' },
              { value: 'USD', label: '$' },
              { value: 'EUR', label: '€' },
              { value: 'KZT', label: '₸' },
            ]}
            {...register('salary_currency')}
          />
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              checked={watch('show_salary')}
              onChange={(e) => setValue('show_salary', e.target.checked)}
              className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            Показывать зарплату в вакансии
          </label>
        </div>
      </Card>

      {/* Опыт, занятость, график */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Требования к кандидату</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Опыт работы"
            options={[
              { value: '', label: 'Не имеет значения' },
              ...Object.entries(EXPERIENCE_LEVEL_LABELS).map(([value, label]) => ({
                value,
                label
              }))
            ]}
            error={errors.experience_level?.message}
            {...register('experience_level')}
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-secondary-700">
              Тип занятости *
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleEmploymentType(value)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    watchEmploymentTypes?.includes(value)
                      ? 'bg-primary-600 text-white'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {errors.employment_types && (
              <p className="mt-1.5 text-sm text-error">{errors.employment_types.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-secondary-700">
              График работы *
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(WORK_SCHEDULE_LABELS).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleWorkSchedule(value)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    watchWorkSchedules?.includes(value)
                      ? 'bg-primary-600 text-white'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {errors.work_schedules && (
              <p className="mt-1.5 text-sm text-error">{errors.work_schedules.message}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Локация */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Местоположение</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <CityAutocomplete
              value={watch('city') || ''}
              onChange={(value) => setValue('city', value, { shouldDirty: true })}
              placeholder="Например, Москва"
            />
            {errors.city?.message && <span className="mt-1 block text-sm text-error">{errors.city.message}</span>}
          </div>

          <Input
            label="Страна"
            placeholder="Россия"
            error={errors.country?.message}
            {...register('country')}
          />

          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-secondary-700">
              <input
                type="checkbox"
                checked={watch('remote_available')}
                onChange={(e) => setValue('remote_available', e.target.checked)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              Возможна удаленная работа
            </label>
          </div>
        </div>
      </Card>

      {/* Навыки */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Ключевые навыки</h3>
        
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Введите навык и нажмите Добавить"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
            className="flex-1"
          />
          <Button type="button" onClick={addSkill} className="flex-shrink-0">
            Добавить
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {watchKeySkills?.map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
            >
              {skill}
              <button
                type="button"
                onClick={() => removeSkill(skill)}
                className="text-primary-400 hover:text-primary-600"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </Card>

      {/* Обязанности */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Обязанности</h3>
        
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Введите обязанность и нажмите Добавить"
            value={newResponsibility}
            onChange={(e) => setNewResponsibility(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addResponsibility())}
            className="flex-1"
          />
          <Button type="button" onClick={addResponsibility} className="flex-shrink-0">
            Добавить
          </Button>
        </div>

        <ul className="space-y-1">
          {watch('responsibilities')?.map((item, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-secondary-600">
              <span>•</span>
              <span className="flex-1">{item}</span>
              <button
                type="button"
                onClick={() => removeResponsibility(index)}
                className="text-error hover:text-error/80"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {/* Требования */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Требования</h3>
        
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Введите требование и нажмите Добавить"
            value={newRequirement}
            onChange={(e) => setNewRequirement(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
            className="flex-1"
          />
          <Button type="button" onClick={addRequirement} className="flex-shrink-0">
            Добавить
          </Button>
        </div>

        <ul className="space-y-1">
          {watch('requirements')?.map((item, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-secondary-600">
              <span>•</span>
              <span className="flex-1">{item}</span>
              <button
                type="button"
                onClick={() => removeRequirement(index)}
                className="text-error hover:text-error/80"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {/* Условия */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Условия работы</h3>
        
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Введите условие и нажмите Добавить"
            value={newCondition}
            onChange={(e) => setNewCondition(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCondition())}
            className="flex-1"
          />
          <Button type="button" onClick={addCondition} className="flex-shrink-0">
            Добавить
          </Button>
        </div>

        <ul className="space-y-1">
          {watch('conditions')?.map((item, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-secondary-600">
              <span>•</span>
              <span className="flex-1">{item}</span>
              <button
                type="button"
                onClick={() => removeCondition(index)}
                className="text-error hover:text-error/80"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {/* Кнопки */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Отмена
          </Button>
        )}
        <Button type="submit" isLoading={isSubmitting || mutation.isPending}>
          {vacancy ? 'Сохранить' : 'Опубликовать вакансию'}
        </Button>
      </div>
    </form>
  )
}
